// Post-processes ESV API HTML so every run of text belongs to a
// <span class="v" data-ref="Neh.1.3">. Highlights, notes and the context menu
// key off that attribute. A verse that continues into a new paragraph gets a
// second span with the same ref.
import { BOOKS } from "@bibleunleashed/shared";
// The plain text of a verse is one walk, in ./highlights, because a highlight
// is a span of characters in that text: two implementations could disagree by
// a space and put the colour in the wrong place.
export { verseText } from "./highlights";

const MARKER = "b.verse-num, b.chapter-num";

/** ESV ids look like v16001003-1: book 16, chapter 1, verse 3, occurrence 1. */
export function refFromMarkerId(id: string): string | undefined {
  const m = /^v(\d{2})(\d{3})(\d{3})/.exec(id);
  if (!m) return undefined;
  const book = BOOKS[Number(m[1]) - 1];
  if (!book) return undefined;
  return `${book.id}.${Number(m[2])}.${Number(m[3])}`;
}

/**
 * The verse marker a paragraph's child node opens with, if any. In prose the
 * marker is the child itself; in poetry the ESV wraps each line in
 * `<span class="line">` with the marker inside it, so a child has to be looked
 * into as well or every quoted psalm keeps the verse number of the line above.
 */
function markerIn(node: ChildNode): Element | undefined {
  if (!(node instanceof Element)) return undefined;
  return node.matches(MARKER) ? node : (node.querySelector(MARKER) ?? undefined);
}

export function wrapVerses(container: Element, chapterOsis: string): void {
  let current: string | undefined;
  const blocks = container.querySelectorAll("p");
  for (const p of blocks) {
    if (p.closest(".footnotes, .crossrefs")) continue;
    let span: HTMLSpanElement | undefined;
    const nodes = Array.from(p.childNodes);
    for (const node of nodes) {
      const marker = markerIn(node);
      if (marker) {
        current = refFromMarkerId(marker.id) ?? current ?? `${chapterOsis}.1`;
        span = document.createElement("span");
        span.className = "v";
        span.dataset.ref = current;
        p.insertBefore(span, node);
        span.appendChild(node);
        continue;
      }
      if (!span) {
        // Text before any marker: the previous verse continuing, or verse 1 when
        // the API omits the first marker.
        current ??= `${chapterOsis}.1`;
        span = document.createElement("span");
        span.className = "v";
        span.dataset.ref = current;
        p.insertBefore(span, node);
      }
      span.appendChild(node);
    }
  }
}

/** ESV marks footnotes "[1]" and cross-references "[a]"; we show the bare letter or number. */
export function tidyMarkers(container: Element): void {
  for (const a of container.querySelectorAll("sup.footnote a, sup.crossref a, .footnotes .footnote > a, .crossrefs .crossref > a")) {
    a.textContent = (a.textContent ?? "").replace(/^\[(.+)\]$/, "$1");
  }
}

const HEADINGS = "h1, h2, h3, h4";
const rank = (el: Element) => Number(el.tagName[1]);

/** The verse an ESV section heading introduces: the first one under it. */
function verseUnder(heading: Element): number | undefined {
  let node: Element | null = heading;
  while ((node = node.nextElementSibling)) {
    if (node.matches(HEADINGS)) continue;
    const span = node.querySelector<HTMLElement>(".v[data-ref]");
    const verse = Number(span?.dataset.ref?.split(".")[2]);
    if (Number.isFinite(verse)) return verse;
  }
  return undefined;
}

/**
 * The ESV's footnote and cross-reference lists, which follow the passage it
 * sent. A page shows the markers in the verses and leaves the lists out.
 */
export function dropNoteLists(container: Element): void {
  for (const block of container.querySelectorAll(".footnotes, .crossrefs")) block.remove();
}

/**
 * Narrow a wrapped chapter to one verse range, for the passage beside a row of
 * commentary. Chapter-wide footnote and cross-reference blocks go too: they
 * belong to the whole chapter, not this slice.
 *
 * A heading stays only with the verse it introduces. Without that, a chapter
 * cut into several rows would repeat "Nehemiah's Prayer" above every row from
 * verse 4 on, because the heading precedes them all in the source.
 */
export function sliceVerses(container: Element, startVerse: number, endVerse: number): void {
  dropNoteLists(container);

  const strayHeadings = Array.from(container.querySelectorAll(HEADINGS)).filter((h) => {
    const verse = verseUnder(h);
    return verse === undefined || verse < startVerse || verse > endVerse;
  });

  for (const span of container.querySelectorAll<HTMLElement>(".v[data-ref]")) {
    const verse = Number(span.dataset.ref?.split(".")[2]);
    if (!Number.isFinite(verse) || verse < startVerse || verse > endVerse) span.remove();
  }
  for (const h of strayHeadings) h.remove();
  for (const p of Array.from(container.querySelectorAll("p"))) {
    if (!p.textContent?.trim()) p.remove();
  }
  // A heading with nothing left under it. Walked backwards so that a heading
  // whose only follower is another discarded heading goes as well.
  for (const h of Array.from(container.querySelectorAll(HEADINGS)).reverse()) {
    const next = h.nextElementSibling;
    if (!next || (next.matches(HEADINGS) && rank(next) <= rank(h))) h.remove();
  }
}
